import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { api } from '@/lib/api'
import type { ContactRelationship, RelationshipType } from '@/lib/types'

export function useContactRelationships(contactId: string | undefined) {
  return useQuery({
    queryKey: ['contact-relationships', contactId],
    queryFn: () =>
      api<{ items: ContactRelationship[] }>(`/api/contacts/${contactId}/relationships`).then(
        (r) => r.items,
      ),
    enabled: !!contactId,
  })
}

export function useCreateContactRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      contact_a_id: string
      contact_b_id: string
      relationship_type?: RelationshipType
      strength?: number
      notes?: string
    }) =>
      api<ContactRelationship>('/api/contact-relationships', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-relationships'] })
      toast.success('Relationship added')
    },
    onError: () => {
      toast.error('Failed to add relationship')
    },
  })
}

export function useUpdateContactRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      relationship_type?: RelationshipType
      strength?: number
      notes?: string | null
    }) =>
      api<ContactRelationship>(`/api/contact-relationships/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-relationships'] })
      toast.success('Relationship updated')
    },
    onError: () => {
      toast.error('Failed to update relationship')
    },
  })
}

export function useDeleteContactRelationship() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/contact-relationships/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-relationships'] })
      toast.success('Relationship removed')
    },
    onError: () => {
      toast.error('Failed to remove relationship')
    },
  })
}
